import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * CloudFlare Turnstile Configuration
 *
 * Sitekeys:
 * - Development/Testing: Use a testing sitekey that always passes
 * - Production: Use your actual Cloudflare Turnstile sitekey
 *
 * Testing sitekeys (always pass):
 * - 1x00000000000000000000AA (visible, always passes)
 * - 2x00000000000000000000AB (invisible, always passes)
 *
 * Testing sitekeys (always fail):
 * - 3x00000000000000000000FF (visible, always fails)
 */
/*

Created from this CloudFlare account: https://dash.cloudflare.com/0636483c41074228b5db52f0c302f718/turnstile
*/

export const TURNSTILE_SITEKEY = '0x4AAAAAAB8Uc80chZjg0Gxl';
export const TURNSTILE_ENABLED = true;

// Declare global turnstile interface
declare global {
  interface Window {
    turnstile?: {
      ready: (callback: () => void) => void;
      render: (
        element: string | HTMLElement,
        options: TurnstileOptions,
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string;
      isExpired: (widgetId: string) => boolean;
    };
  }
}

export interface TurnstileOptions {
  sitekey: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  callback?: (token: string) => void;
  'error-callback'?: (errorCode: string) => void;
  'expired-callback'?: () => void;
  execution?: 'render' | 'execute';
  appearance?: 'always' | 'execute' | 'interaction-only';
}

export interface UseTurnstileOptions {
  onSuccess?: (token: string) => void;
  onError?: (errorCode: string) => void;
  onExpired?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
}

export interface UseTurnstileReturn {
  widgetId: string | null;
  token: string | null;
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
  render: (containerElement: HTMLElement | string) => void;
  reset: () => void;
  remove: () => void;
}

export function useTurnstile(
  options: UseTurnstileOptions = {},
): UseTurnstileReturn {
  const {
    onSuccess,
    onError,
    onExpired,
    theme = 'auto',
    size = 'normal',
  } = options;

  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep refs to avoid recreating callbacks on every render
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onExpiredRef = useRef(onExpired);

  // Update refs when callbacks change
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onExpiredRef.current = onExpired;
  }, [onExpired]);

  // Check if Turnstile is ready
  // Since we're loading with async, we just poll for window.turnstile
  // Don't use turnstile.ready() with async loading - it causes warnings
  useEffect(() => {
    if (!TURNSTILE_ENABLED) {
      setIsReady(true);
      return;
    }

    let mounted = true;

    const checkTurnstileReady = () => {
      if (!mounted) return;

      if (window.turnstile) {
        setIsReady(true);
      } else {
        // Retry after a short delay if Turnstile script hasn't loaded yet
        setTimeout(checkTurnstileReady, 100);
      }
    };

    checkTurnstileReady();

    return () => {
      mounted = false;
    };
  }, []);

  const render = useCallback(
    (containerElement: HTMLElement | string) => {
      // If Turnstile is disabled, immediately call success callback with a dummy token
      if (!TURNSTILE_ENABLED) {
        const dummyToken = 'turnstile-disabled';
        setToken(dummyToken);
        if (onSuccessRef.current) {
          onSuccessRef.current(dummyToken);
        }
        return;
      }

      if (!isReady || !window.turnstile) {
        console.warn('Turnstile is not ready yet');
        return;
      }

      if (widgetId) {
        console.warn(
          'Widget already rendered. Call reset() or remove() first.',
        );
        return;
      }

      setIsLoading(true);
      setError(null);
      setToken(null);

      try {
        const newWidgetId = window.turnstile.render(containerElement, {
          sitekey: TURNSTILE_SITEKEY,
          theme,
          size,
          callback: (token: string) => {
            setToken(token);
            setIsLoading(false);
            if (onSuccessRef.current) {
              onSuccessRef.current(token);
            }
          },
          'error-callback': (errorCode: string) => {
            setError(errorCode);
            setIsLoading(false);
            if (onErrorRef.current) {
              onErrorRef.current(errorCode);
            }
          },
          'expired-callback': () => {
            setToken(null);
            setError('Token expired');
            if (onExpiredRef.current) {
              onExpiredRef.current();
            }
          },
        });

        setWidgetId(newWidgetId);
      } catch (err) {
        console.error('Error rendering Turnstile widget:', err);
        setError('Failed to render widget');
        setIsLoading(false);
      }
    },
    [isReady, widgetId, theme, size],
  );

  const reset = useCallback(() => {
    if (!TURNSTILE_ENABLED) {
      return;
    }

    if (widgetId && window.turnstile) {
      window.turnstile.reset(widgetId);
      setToken(null);
      setError(null);
      setIsLoading(true);
    }
  }, [widgetId]);

  const remove = useCallback(() => {
    if (!TURNSTILE_ENABLED) {
      return;
    }

    if (widgetId && window.turnstile) {
      window.turnstile.remove(widgetId);
      setWidgetId(null);
      setToken(null);
      setError(null);
      setIsLoading(false);
    }
  }, [widgetId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      remove();
    };
  }, [remove]);

  return {
    widgetId,
    token,
    isReady,
    isLoading,
    error,
    render,
    reset,
    remove,
  };
}
